import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Text, Modal, Button, Navigation, Cell, Image, Divider, DeprecatedIcon, ColorCode } from '@vinted/web-ui';
import useTranslate from 'hooks/useTranslate';
import useIsDarkMode from 'hooks/useIsDarkMode';
import useFetch from 'hooks/useFetch';
import ContentLoader from 'components/ContentLoader';
import ScrollableArea from 'components/ScrollableArea';
import { formatCurrencyAmount } from 'libs/utils/formatString';
import { getLocale } from 'state/intl/selectors';
import { transformFeaturedCollectionPricingResponse } from 'data/api/transformers/response';
import { getFeaturedCollectionPricing } from 'data/api';
import { getAssetPath } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const FeaturedCollectionOnboardingModal = _ref => {
  let {
    show,
    isFeaturedCollectionCreated,
    onNextAction,
    onBack
  } = _ref;
  const translate = useTranslate('featured_collection.onboarding_modal');
  const isDarkMode = useIsDarkMode();
  const locale = useSelector(getLocale);
  const {
    fetch: fetchPricing,
    isLoading,
    transformedData: pricing,
    error
  } = useFetch(getFeaturedCollectionPricing, transformFeaturedCollectionPricingResponse);
  const isFetchPricingInitiated = !!pricing || isLoading || error;
  useEffect(() => {
    if (!show || isFetchPricingInitiated) return;
    fetchPricing();
  }, [fetchPricing, show, isFetchPricingInitiated]);

  function renderNavigation() {
    return /*#__PURE__*/_jsx(Navigation, {
      right: /*#__PURE__*/_jsx(Button, {
        deprecatedIconName: "x",
        theme: "amplified",
        styling: Button.Styling.Flat,
        testId: "featured-collection-onboarding-back-button",
        onClick: onBack
      }),
      body: /*#__PURE__*/_jsx(Text, {
        type: Text.Type.Title,
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center,
        text: translate('title')
      })
    });
  }

  function renderConfirmationAction() {
    const confirmationKey = isFeaturedCollectionCreated ? 'review' : 'create';
    return /*#__PURE__*/_jsx("div", {
      className: "u-ui-margin-horizontal-large u-ui-margin-bottom-large",
      children: /*#__PURE__*/_jsx(Button, {
        text: translate("actions.".concat(confirmationKey)),
        styling: Button.Styling.Filled,
        onClick: onNextAction,
        testId: "featured-collection-onboarding-".concat(confirmationKey, "-button")
      })
    });
  }

  function renderIntro() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsx(Text, {
          text: translate('intro.title'),
          type: Text.Type.Heading
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.profile.title'),
        body: translate('intro.profile.body'),
        prefix: /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "stack",
          color: ColorCode.GreyscaleLevel3
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.homepage.title'),
        body: translate('intro.homepage.body'),
        prefix: /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "bookmark",
          color: ColorCode.GreyscaleLevel3
        })
      }), /*#__PURE__*/_jsx(Cell, {
        title: translate('intro.sale.title'),
        body: translate('intro.sale.body'),
        prefix: /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "percent",
          color: ColorCode.GreyscaleLevel3
        })
      })]
    });
  }

  function renderDeal() {
    if (!pricing) return null;
    const pricePerDay = formatCurrencyAmount(pricing.pricePerDay, locale);
    const totalPrice = formatCurrencyAmount(pricing.totalPrice, locale);
    return /*#__PURE__*/_jsx(Cell, {
      title: translate('duration_day_count', {
        count: pricing.effectiveDays
      }, {
        count: pricing.effectiveDays
      }),
      body: translate('price_per_day', {
        price: pricePerDay
      }),
      suffix: /*#__PURE__*/_jsx(Text, {
        text: totalPrice,
        type: Text.Type.Title
      })
    });
  }

  function renderContent() {
    if (isLoading) {
      return /*#__PURE__*/_jsx(ContentLoader, {
        styling: ContentLoader.Styling.Wide
      });
    }

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Image, {
        src: getAssetPath('featured-collection-onboarding.png', isDarkMode),
        testId: "featured-collection-onboarding-image"
      }), renderIntro(), /*#__PURE__*/_jsx(Divider, {}), renderDeal(), renderConfirmationAction()]
    });
  }

  return /*#__PURE__*/_jsx(Modal, {
    show: show,
    hasScrollableContent: true,
    testId: "featured-collection-onboarding-modal",
    children: /*#__PURE__*/_jsx("div", {
      className: "u-fill-width",
      children: /*#__PURE__*/_jsxs(ScrollableArea, {
        children: [renderNavigation(), renderContent()]
      })
    })
  });
};

export default FeaturedCollectionOnboardingModal;